import React from 'react'
import { Link } from 'react-router'

import cx from 'helpers/cx'

export type ButtonVariant =
  | 'primary'
  | 'accent'
  | 'cancel'
  | 'nature'
  | 'danger'
  | 'ghostAccent'
  | 'textLink'

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'input'

type ButtonExtras = {
  variant?: ButtonVariant
  size?: ButtonSize
  block?: boolean
  selected?: boolean
  loading?: boolean
  loadingText?: string
  withIcon?: boolean
  disabled?: boolean
  children?: React.ReactNode
}

export type ButtonAsButton = ButtonExtras &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonExtras> & {
    to?: never
    href?: never
  }

export type ButtonAsLink = ButtonExtras &
  Omit<React.ComponentProps<typeof Link>, keyof ButtonExtras | 'to'> & {
    to: React.ComponentProps<typeof Link>['to']
    type?: never
    href?: never
  }

export type ButtonAsAnchor = ButtonExtras &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonExtras> & {
    href: string
    to?: never
    type?: never
  }

export type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor

const VARIANT_CLASS: Record<Exclude<ButtonVariant, 'textLink'>, string> = {
  primary: 'btn-primary',
  accent: 'btn-accent',
  cancel: 'btn-cancel',
  nature: 'btn-nature',
  danger: 'btn-danger',
  ghostAccent: 'btn-ghost-accent',
}

const SIZE_CLASS: Record<ButtonSize, string> = {
  xs: 'btn-xs',
  sm: 'btn-sm',
  md: 'btn-md',
  lg: 'btn-lg',
  input: 'btn-input',
}

const getVariantClass = (variant: ButtonVariant, size: ButtonSize) => {
  if (variant === 'textLink') return size === 'xs' ? 'btn-text-link-xs' : 'btn-text-link'
  return VARIANT_CLASS[variant]
}

const Button = ({
  variant = 'primary',
  size = 'md',
  block = false,
  selected = false,
  to,
  href,
  loading = false,
  loadingText,
  withIcon = false,
  className,
  disabled,
  type = 'button',
  children,
  ...rest
}: ButtonProps) => {
  /* --------------------------------- Getters -------------------------------- */

  const isDisabled = disabled || loading
  const content = loading && loadingText ? loadingText : children
  const buttonClassName = cx(
    'btn',
    getVariantClass(variant, size),
    variant !== 'textLink' && SIZE_CLASS[size],
    block && 'btn-block',
    selected && 'btn-ghost-accent-selected',
    withIcon && 'btn-with-icon',
    className
  )

  /* ---------------------------------- Doms ---------------------------------- */

  if (href) {
    const anchorProps = rest as Omit<ButtonAsAnchor, keyof ButtonExtras | 'href'>
    if (isDisabled) {
      return (
        <span className={buttonClassName} aria-disabled="true" tabIndex={-1} {...anchorProps}>
          {content}
        </span>
      )
    }
    return (
      <a href={href} className={buttonClassName} {...anchorProps}>
        {content}
      </a>
    )
  }

  if (to) {
    const linkProps = rest as Omit<ButtonAsLink, keyof ButtonExtras | 'to'>
    if (isDisabled) {
      return (
        <span className={buttonClassName} aria-disabled="true" tabIndex={-1} {...linkProps}>
          {content}
        </span>
      )
    }
    return (
      <Link to={to} className={buttonClassName} {...linkProps}>
        {content}
      </Link>
    )
  }

  const buttonProps = rest as Omit<ButtonAsButton, keyof ButtonExtras>
  return (
    <button type={type} className={buttonClassName} disabled={isDisabled} {...buttonProps}>
      {content}
    </button>
  )
}

export { Button }
